import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _get from 'lodash/get';
import auth from '@udacity/ureact-hoth';

import { useCreateMentorMutation, useGetMentorQuery, useUpdateMentorMutation } from 'app/mentor/queries/enhanced';
import useApplicationRedirect from 'app/common/views/application-redirect';
import { LoadingPage } from 'app/common/views/loading-page';
import { ProfileEditor } from 'app/common/views/profile-editor';
import { EditorType } from 'app/common/views/profile-editor/form-types';
import { REQUIRED_FIELDS } from 'app/common/views/profile-editor/form-helper';
import { MentorDash_CreateMentorMutationVariables } from 'app/mentor/queries/create-mentor.generated';

export const ProfileEditorContainer: React.FC = () => {
  const uid = auth.getCurrentUserId() || '';
  const { isLoading: isMentorLoading } = useGetMentorQuery({ uid }, { skip: !uid });

  // The editor seeds form state and redirects from GetMentor on mount.
  return isMentorLoading ? <LoadingPage /> : <ProfileEditorRoutes />;
};

const ProfileEditorRoutes: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const uid = auth.getCurrentUserId() || '';
  const { data } = useGetMentorQuery({ uid }, { skip: !uid });
  const mentor = data?.mentor;
  const hasMentorAccount = Boolean(mentor?.uid);
  const [createMentor] = useCreateMentorMutation();
  const [updateMentor] = useUpdateMentorMutation();

  const { pathname } = useLocation();
  const history = useHistory();

  useApplicationRedirect(pathname, {
    hasMentorAccount,
    application: mentor?.application,
  });

  const handleSubmitWithCreate = async (form: MentorDash_CreateMentorMutationVariables, path: string) => {
    try {
      setIsLoading(true);
      await createMentor(form).unwrap();
      history.push(path);
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitWithUpdate = async (form: MentorDash_CreateMentorMutationVariables, path: string) => {
    try {
      setIsLoading(true);
      await updateMentor(form).unwrap();
      history.push(path);
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  switch (pathname) {
    case '/specialized-mentor/apply': {
      const appliedForSpecialized = _get(mentor, ['application', 'services'], []).includes('government_mentor');
      if (hasMentorAccount && !appliedForSpecialized) {
        return (
          <ProfileEditor
            editorType={EditorType.AbbreviatedSpecialized}
            isLoading={isLoading}
            handleSubmit={(form) => {
              handleSubmitWithUpdate(form, '/in-review');
            }}
            requiredFields={REQUIRED_FIELDS.abbreviatedSpecialized}
          />
        );
      } else {
        return (
          <ProfileEditor
            editorType={EditorType.Specialized}
            isLoading={isLoading}
            handleSubmit={(form) => {
              handleSubmitWithCreate(form, '/in-review');
            }}
            requiredFields={REQUIRED_FIELDS.specialized}
          />
        );
      }
    }
    case '/specialized-mentor/update': {
      return (
        <ProfileEditor
          editorType={EditorType.Specialized}
          isLoading={isLoading}
          handleSubmit={(form) => {
            handleSubmitWithUpdate(form, '/in-review');
          }}
          requiredFields={REQUIRED_FIELDS.specialized}
        />
      );
    }
    case '/update': {
      return (
        <ProfileEditor
          editorType={EditorType.Update}
          isLoading={isLoading}
          handleSubmit={(form) => {
            handleSubmitWithUpdate(form, '/queue/certifications');
          }}
          requiredFields={REQUIRED_FIELDS.update}
        />
      );
    }
    default: {
      return (
        <ProfileEditor
          editorType={EditorType.Standard}
          isLoading={isLoading}
          handleSubmit={(form) => {
            handleSubmitWithCreate(form, '/queue/certifications');
          }}
          requiredFields={REQUIRED_FIELDS.standard}
        />
      );
    }
  }
};
